import cx from 'classnames';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Sticky from 'react-sticky-el';

import useIsMobile from '../../../../../hooks/use-is-mobile';
import useLinkProps from '../../../../../hooks/use-link-props';
import { currentSelectedCategory } from '../../../../../state/select-part-and-category';
import CategoryNavigationLight from '../../../categories/navigation-light';
import TextInput from '../../../v2/inputs/text';

import FilteredList from './filtered-list';
import { useCurrentGroupInfo } from './use-current-group-info';
import { useFilteredGroups } from './use-filtered-groups';

type PartsContentProps = {
  groups: TCategories;
  currentPart: string;
};

const PartsContent = (
  {
    groups,
    currentPart,
    ref
  }: PartsContentProps
) => {
  const { locale = 'en', push, query } = useRouter();
  const { t } = useTranslation();
  const refInput = useRef(null);
  const [search, setSearch] = useState('');
  const filteredGroups = useFilteredGroups({ groups, search });
  const current = useCurrentGroupInfo({ groups, currentPart });
  const { setValue } = useFormContext();
  const isMobile = useIsMobile();
  const getLink = useLinkProps();
  const innerRef = ref || refInput;
  const setCurrentSelectedCategory = useSetAtom(currentSelectedCategory);

  useEffect(() => {
    setSearch('');
  }, [locale]);

  const props = useMemo(
    () => ({
      groups: filteredGroups,
      current,
      onChange: (part: TPart) => {
        if (part.total_products <= 0) {
          return;
        }
        if (!isMobile) {
          setValue('category', part.subgroup_slug);
          setValue('subCategory', part.slug);
        }
        setSearch('');
        const { category, subCategory, page, ...rest } = query;
        const link = getLink('search', {
          ...rest,
          category: part.subgroup_slug,
          subCategory: part.slug,
        });
        setCurrentSelectedCategory({
          category: part.subgroup_slug,
          subCategory: part.slug,
        });

        push(link.href, link.as, { shallow: true, scroll: true });
      },
    }),
    [
      filteredGroups,
      current,
      isMobile,
      query,
      getLink,
      setCurrentSelectedCategory,
      push,
      setValue,
    ]
  );

  const isInputDisabled = groups.filter((el) => el.total_products > 0);

  return (
    <>
      <Sticky
        mode="top"
        boundaryElement=".hybridSavedCarTitle"
        hideOnBoundaryHit={false}
        wrapperClassName={cx('!pt-6 flex flex-col gap-4 flex')}
        stickyClassName={cx('z-10 bg-white')}
      >
        <div className={cx('group bg-white')} data-cy="part-selection">
          <TextInput
            ref={innerRef}
            label={t('common:categories.label.subCategory.other')}
            placeholder={t('common:filter-parts')}
            value={search}
            disabled={isInputDisabled.length === 0}
            border
            onChange={(e) => setSearch(e.target.value)}
            clearButton
            onClear={() => {
              setSearch('');
            }}
            onFocus={() => {
              // window.scrollTo(0, 0);
            }}
            focusBg
            showExtraIcon={true}
            scrollToTop={true}
            variant="filter"
          //data-cy="search-input"
          />
        </div>
      </Sticky>
      {search ? (
        <FilteredList searchText={search} {...props} showStock={false} />
      ) : (
        <div className="flex-1 overflow-auto overflow-x-hidden ">
          <CategoryNavigationLight {...props} />
        </div>
      )}
    </>
  );
};

PartsContent.displayName = 'PartsContent';

export default PartsContent;
